import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  signupLawyerSchema,
  type SignupLawyerInput,
  COGNITO_GROUPS,
  type Specialty,
} from '@parecer/shared';
import { Award, FileText, IdCard, MapPin, ShieldCheck, User } from 'lucide-react';
import { SpecialtyPicker } from '@/components/SpecialtyPicker';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cognitoSignUp, mapCognitoErrorMessage } from '@/lib/cognito';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

const UF_LIST = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

interface ViaCepResponse {
  cep?: string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean;
}

export function CadastroAdvogado() {
  const [loading, setLoading] = useState(false);
  const [selectedSpecs, setSelectedSpecs] = useState<Specialty[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [cepLoading, setCepLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<SignupLawyerInput>({
    resolver: zodResolver(signupLawyerSchema),
    defaultValues: {
      email: '',
      password: '',
      name: '',
      cpf: '',
      oab: '',
      oabState: '',
      bio: '',
      specialties: [],
      address: {
        cep: '',
        street: '',
        number: '',
        complement: '',
        district: '',
        city: '',
        state: '',
      },
    } as Partial<SignupLawyerInput> as SignupLawyerInput,
  });

  function setSpecs(next: Specialty[]) {
    setSelectedSpecs(next);
    form.setValue('specialties', next, { shouldValidate: true });
  }
  function setTags(next: string[]) {
    setSelectedTags(next);
    form.setValue('tags', next as never, { shouldValidate: true });
  }

  /**
   * On CEP blur — query ViaCEP, fill street/district/city/state. Lawyer can
   * still edit anything that came back. We swallow network errors silently
   * (lawyer just fills manually if ViaCEP is down).
   */
  async function handleCepBlur(): Promise<void> {
    const raw = (form.getValues('address.cep') || '').replace(/\D/g, '');
    if (raw.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
      if (!r.ok) return;
      const data = (await r.json()) as ViaCepResponse;
      if (data.erro) return;
      if (data.logradouro) form.setValue('address.street', data.logradouro, { shouldValidate: true });
      if (data.bairro) form.setValue('address.district', data.bairro, { shouldValidate: true });
      if (data.localidade) form.setValue('address.city', data.localidade, { shouldValidate: true });
      if (data.uf) form.setValue('address.state', data.uf.toUpperCase(), { shouldValidate: true });
    } catch {
      // ViaCEP unreachable — proceed manually
    } finally {
      setCepLoading(false);
    }
  }

  async function onSubmit(values: SignupLawyerInput) {
    setLoading(true);
    try {
      // Cognito only takes a handful of attributes — everything else goes to
      // localStorage and gets PATCHed on first login by the lawyer dashboard.
      await cognitoSignUp({
        email: values.email,
        password: values.password,
        attributes: {
          email: values.email,
          given_name: values.name,
          'custom:role': COGNITO_GROUPS.LAWYERS,
          'custom:oab': values.oab,
          'custom:oabState': values.oabState,
          ...(values.phone ? { phone_number: values.phone } : {}),
        },
      });
      try {
        const payload = {
          bio: values.bio,
          specialties: values.specialties,
          ...(selectedTags.length > 0 ? { tags: selectedTags } : {}),
          ...(values.linkedinUrl?.trim() ? { linkedinUrl: values.linkedinUrl.trim() } : {}),
          cpf: values.cpf,
          ...(values.cnpj?.trim() ? { cnpj: values.cnpj.trim() } : {}),
          oab: values.oab,
          oabState: values.oabState,
          ...(values.oabCardFileUrl?.trim() ? { oabCardFileUrl: values.oabCardFileUrl.trim() } : {}),
          ...(values.oabRegularityCertFileUrl?.trim()
            ? { oabRegularityCertFileUrl: values.oabRegularityCertFileUrl.trim() }
            : {}),
          ...(values.professionalPhotoUrl?.trim()
            ? { professionalPhotoUrl: values.professionalPhotoUrl.trim() }
            : {}),
          address: values.address,
          acceptedEthicsCode: true as const,
          acceptedPlatformTerms: true as const,
          declaredNoImpediment: true as const,
          acceptedAt: new Date().toISOString(),
        };
        window.localStorage.setItem(
          `pendingLawyerOnboarding:${values.email.toLowerCase()}`,
          JSON.stringify(payload),
        );
      } catch {
        // localStorage off (private mode) — lawyer fills in /lawyer/perfil after login.
      }
      toast({
        variant: 'success',
        title: 'Cadastro enviado',
        description: 'Confirme seu email. Sua OAB passará por verificação manual.',
      });
      navigate(`/confirmar?email=${encodeURIComponent(values.email)}&role=lawyer`);
    } catch (err) {
      toast({ variant: 'danger', title: 'Falha no cadastro', description: mapCognitoErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-3xl py-16">
        <div className="mb-8">
          <Badge variant="accent" className="mb-2">Cadastro de advogado</Badge>
          <h1 className="font-serif text-display-lg">Cadastre-se como advogado parceiro</h1>
          <p className="text-muted-foreground mt-2">
            Cadastro completo permite verificação mais rápida pela equipe Parecer.ai
            e maior credibilidade junto aos clientes.
          </p>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* === 1. Identidade === */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4 text-accent" /> Identidade
              </CardTitle>
              <CardDescription>Dados pessoais e contato.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nome completo</Label>
                  <Input id="name" autoComplete="name" className="mt-1.5" {...form.register('name')} />
                  {form.formState.errors.name && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.name.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email">Email profissional</Label>
                  <Input id="email" type="email" autoComplete="email" className="mt-1.5" {...form.register('email')} />
                  {form.formState.errors.email && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="cpf">CPF (apenas dígitos)</Label>
                  <Input
                    id="cpf"
                    inputMode="numeric"
                    placeholder="00000000000"
                    maxLength={11}
                    className="mt-1.5"
                    {...form.register('cpf')}
                  />
                  {form.formState.errors.cpf && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.cpf.message}</p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Necessário pra pagamentos via Pagar.me e emissão de NF.
                  </p>
                </div>
                <div>
                  <Label htmlFor="phone">Celular (opcional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+5511999999999"
                    className="mt-1.5"
                    {...form.register('phone')}
                  />
                  {form.formState.errors.phone && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.phone.message}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="cnpj">CNPJ (opcional — sociedade de advogados ou advogado PJ)</Label>
                  <Input
                    id="cnpj"
                    inputMode="numeric"
                    placeholder="00000000000000"
                    maxLength={14}
                    className="mt-1.5"
                    {...form.register('cnpj')}
                  />
                  {form.formState.errors.cnpj && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.cnpj.message}</p>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  className="mt-1.5"
                  {...form.register('password')}
                />
                {form.formState.errors.password && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.password.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* === 2. OAB === */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <IdCard className="h-4 w-4 text-accent" /> Registro OAB
              </CardTitle>
              <CardDescription>
                Verificamos manualmente pela CNA da OAB. Anexar carteira e certidão de regularidade
                acelera o processo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="oab">Número da OAB</Label>
                  <Input id="oab" inputMode="numeric" className="mt-1.5" {...form.register('oab')} />
                  {form.formState.errors.oab && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.oab.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="oabState">Seção</Label>
                  <select
                    id="oabState"
                    className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    {...form.register('oabState')}
                  >
                    <option value="">UF</option>
                    {UF_LIST.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                  {form.formState.errors.oabState && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.oabState.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="oabCardFileUrl">Link da carteira da OAB (opcional)</Label>
                <Input
                  id="oabCardFileUrl"
                  type="url"
                  placeholder="https://drive.google.com/file/d/.../view"
                  className="mt-1.5"
                  {...form.register('oabCardFileUrl')}
                />
                {form.formState.errors.oabCardFileUrl && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.oabCardFileUrl.message}</p>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">
                  Drive/OneDrive com link público. Upload direto na plataforma virá em breve.
                </p>
              </div>

              <div>
                <Label htmlFor="oabRegularityCertFileUrl">
                  Certidão de regularidade OAB (opcional)
                </Label>
                <Input
                  id="oabRegularityCertFileUrl"
                  type="url"
                  placeholder="https://..."
                  className="mt-1.5"
                  {...form.register('oabRegularityCertFileUrl')}
                />
                {form.formState.errors.oabRegularityCertFileUrl && (
                  <p className="text-xs text-danger mt-1">
                    {form.formState.errors.oabRegularityCertFileUrl.message}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">
                  PDF emitido pelo CNA (Cadastro Nacional de Advogados). Auto-verificável em breve.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* === 3. Endereço profissional === */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4 text-accent" /> Endereço profissional
              </CardTitle>
              <CardDescription>
                Obrigatório pela OAB (Provimento 205/2021). Não exibimos publicamente — uso interno
                pra emissão de NF e contato.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-4 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="cep">CEP</Label>
                  <Input
                    id="cep"
                    inputMode="numeric"
                    placeholder="01310100"
                    maxLength={8}
                    className="mt-1.5"
                    {...form.register('address.cep', { onBlur: handleCepBlur })}
                  />
                  {form.formState.errors.address?.cep && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.address.cep.message}</p>
                  )}
                  {cepLoading && (
                    <p className="text-[11px] text-muted-foreground mt-1">Buscando…</p>
                  )}
                </div>
              </div>
              <div className="grid sm:grid-cols-6 gap-4">
                <div className="sm:col-span-4">
                  <Label htmlFor="street">Logradouro</Label>
                  <Input id="street" className="mt-1.5" {...form.register('address.street')} />
                  {form.formState.errors.address?.street && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.address.street.message}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="number">Número</Label>
                  <Input id="number" className="mt-1.5" {...form.register('address.number')} />
                  {form.formState.errors.address?.number && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.address.number.message}</p>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="complement">Complemento (opcional)</Label>
                <Input
                  id="complement"
                  placeholder="Sala 502, andar 5, conjunto B…"
                  className="mt-1.5"
                  {...form.register('address.complement')}
                />
              </div>
              <div className="grid sm:grid-cols-6 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="district">Bairro</Label>
                  <Input id="district" className="mt-1.5" {...form.register('address.district')} />
                  {form.formState.errors.address?.district && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.address.district.message}</p>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <Label htmlFor="city">Cidade</Label>
                  <Input id="city" className="mt-1.5" {...form.register('address.city')} />
                  {form.formState.errors.address?.city && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.address.city.message}</p>
                  )}
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="state">UF</Label>
                  <select
                    id="state"
                    className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    {...form.register('address.state')}
                  >
                    <option value="">UF</option>
                    {UF_LIST.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                  {form.formState.errors.address?.state && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.address.state.message}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* === 4. Perfil profissional === */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Award className="h-4 w-4 text-accent" /> Perfil profissional
              </CardTitle>
              <CardDescription>
                Bio, especialidades e foto profissional aparecem no card público do marketplace.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="linkedinUrl">LinkedIn (opcional)</Label>
                <Input
                  id="linkedinUrl"
                  type="url"
                  placeholder="https://linkedin.com/in/..."
                  className="mt-1.5"
                  {...form.register('linkedinUrl')}
                />
                {form.formState.errors.linkedinUrl && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.linkedinUrl.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="bio">Bio profissional</Label>
                <textarea
                  id="bio"
                  rows={6}
                  className="mt-1.5 flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Mínimo 200 caracteres. Trajetória, escritórios, área principal, formação, casos relevantes (sem PII), diferenciais."
                  {...form.register('bio')}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {(form.watch('bio') ?? '').length} / 2000 caracteres (mínimo 200)
                </p>
                {form.formState.errors.bio && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.bio.message}</p>
                )}
              </div>

              <div>
                <Label>Especialidades (selecione 1 a 15) e tags livres</Label>
                <div className="mt-2">
                  <SpecialtyPicker
                    selected={selectedSpecs}
                    onChange={setSpecs}
                    tags={selectedTags}
                    onTagsChange={setTags}
                  />
                </div>
                {form.formState.errors.specialties && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.specialties.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="professionalPhotoUrl">
                  Foto profissional (opcional)
                </Label>
                <Input
                  id="professionalPhotoUrl"
                  type="url"
                  placeholder="https://drive.google.com/.../uc?id=..."
                  className="mt-1.5"
                  {...form.register('professionalPhotoUrl')}
                />
                {form.formState.errors.professionalPhotoUrl && (
                  <p className="text-xs text-danger mt-1">
                    {form.formState.errors.professionalPhotoUrl.message}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">
                  <strong>Diretrizes OAB (Prov. 205/2021):</strong> roupa formal, fundo neutro,
                  expressão sóbria, sem elementos publicitários ou imagens casuais.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* === 5. Aceites e declarações === */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-accent" /> Aceites e declarações
              </CardTitle>
              <CardDescription>
                Obrigatórios pra atuar como advogado parceiro no Parecer.ai.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:border-accent/40 transition-colors">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-border accent-accent flex-none"
                  {...form.register('acceptedEthicsCode')}
                />
                <div className="text-sm">
                  <p className="font-medium">Aceito o Código de Ética e Disciplina da OAB</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Comprometo-me a observar o Código de Ética e Disciplina da OAB
                    (Resolução 02/2015) em todas as atuações realizadas pela plataforma.
                  </p>
                </div>
              </label>
              {form.formState.errors.acceptedEthicsCode && (
                <p className="text-xs text-danger">{form.formState.errors.acceptedEthicsCode.message}</p>
              )}

              <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:border-accent/40 transition-colors">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-border accent-accent flex-none"
                  {...form.register('acceptedPlatformTerms')}
                />
                <div className="text-sm">
                  <p className="font-medium">Aceito o Termo de Parceria da Parecer.ai</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Concordo com a divisão 85% advogado / 15% plataforma, escrow via Pagar.me,
                    e demais cláusulas do{' '}
                    <Link to="/termos-parceria" className="text-accent hover:underline">termo completo</Link>.
                  </p>
                </div>
              </label>
              {form.formState.errors.acceptedPlatformTerms && (
                <p className="text-xs text-danger">{form.formState.errors.acceptedPlatformTerms.message}</p>
              )}

              <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:border-accent/40 transition-colors">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-border accent-accent flex-none"
                  {...form.register('declaredNoImpediment')}
                />
                <div className="text-sm">
                  <p className="font-medium">Declaração de não impedimento</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Declaro, sob as penas da lei, que não estou suspenso ou impedido do exercício
                    da advocacia, e que não tenho processo ético-disciplinar pendente perante a OAB.
                  </p>
                </div>
              </label>
              {form.formState.errors.declaredNoImpediment && (
                <p className="text-xs text-danger">{form.formState.errors.declaredNoImpediment.message}</p>
              )}
            </CardContent>
          </Card>

          {/* === Resumo de pendências de upload (informativo) === */}
          <Card className="border-dashed">
            <CardContent className="pt-6 flex items-start gap-3 text-sm">
              <FileText className="h-5 w-5 text-muted-foreground flex-none mt-0.5" />
              <div>
                <p className="font-medium">Upload nativo de documentos virá em breve.</p>
                <p className="text-muted-foreground text-xs mt-1">
                  Por enquanto, anexe os PDFs pelo Google Drive/OneDrive e cole o link público.
                  Após cadastro e primeiro login você poderá subir tudo direto pela plataforma.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3">
            <Button type="submit" variant="accent" className="w-full" loading={loading}>
              Criar conta de advogado
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Já tem conta?{' '}
              <Link to="/login" className="text-foreground font-medium hover:underline">
                Entrar
              </Link>
            </p>
          </div>
        </form>
      </div>
    </MarketingLayout>
  );
}
